// src/hooks/useModalTableState.ts
import { useMemo, useState, useEffect } from 'react';

type Opts<T> = {
  /** как понять, что запись непрочитана (по умолчанию (r as any).isRead === false) */
  unreadPredicate?: (row: T) => boolean;
  /** функция фильтрации по строке поиска */
  filterFn: (row: T, q: string) => boolean;
  /** задержка перед фильтрацией (мс) */
  debounceMs?: number;
};

/**
 * Общее состояние модалки с таблицей: выбранные строки, поиск, фильтрация,
 * флаги anySelected/anyUnreadSelected.
 */
export function useModalTableState<T>(
  rows: T[],
  { unreadPredicate, filterFn, debounceMs = 0 }: Opts<T>
) {
  const [selected, setSelected] = useState<React.Key[]>([]);
  const [search, setSearch] = useState('');

  // дебаунс поиска (по желанию)
  const [qDebounced, setQDebounced] = useState(search);
  useEffect(() => {
    if (!debounceMs) { setQDebounced(search); return; }
    const id = setTimeout(() => setQDebounced(search), debounceMs);
    return () => clearTimeout(id);
  }, [search, debounceMs]);

  // отфильтрованные данные
  const data = useMemo(() => {
    const q = qDebounced.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => filterFn(r, q));
  }, [rows, qDebounced, filterFn]);

  // стабильный предикат "непрочитано", чтобы не дрожали зависимости
  const isUnread = useMemo(
    () => unreadPredicate ?? ((r: any) => r?.isRead === false),
    [unreadPredicate]
  );

  // есть ли среди выбранных непрочитанные
  const anyUnreadSelected = useMemo(
    () => data.some((d: any) => isUnread(d) && selected.includes((d as any).id)),
    [data, selected, isUnread]
  );

  const anySelected = selected.length > 0;

  return {
    // данные
    data,
    // выбор
    selected,
    setSelected,
    // поиск
    search,
    setSearch,
    // признаки
    anySelected,
    anyUnreadSelected,
  };
}
